import { useEffect, useState } from 'react';

import { useStore } from '../stores-v2/StoreContext';
import { fetchSpaCredentials } from '../utility/fetchSpaCredentials';
import { hasSpaCredentials } from '../utility/spaCredentials';

import type { DatoContext } from '../stores-v2/types';
import type { SessionResource, UserResource } from '@clerk/types';

interface InitParams {
  storyId: string | null | undefined;
  videoId?: string;
  showcase?: string;
  env?: string;
  datoCtx?: DatoContext;
  clerkUser?: UserResource | null;
  clerkSession?: SessionResource | null;
  isClerkLoaded?: boolean;
  hasValidAuth: boolean;
}

export function useStoreInitialization({
  env,
  datoCtx,
  clerkUser,
  clerkSession,
  isClerkLoaded,
  hasValidAuth,
}: InitParams) {
  const rootStore = useStore();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!hasValidAuth) return;

    console.debug('Initializing store with:', {
      env,
      datoCtx,
      clerkUser,
      clerkSession,
    });

    async function initializeStore() {
      // MCP headless mode: use injected auth instead of Clerk
      const mcpAuth = window.__ARBOR_MCP_AUTH;
      if (mcpAuth?.token) {
        rootStore.userIdentityStore.setClerkContext(
          {
            id: mcpAuth.userId,
            primaryEmailAddress: { emailAddress: mcpAuth.email },
            fullName: mcpAuth.email,
            emailAddresses: [{ emailAddress: mcpAuth.email }],
            organizationMemberships: mcpAuth.orgId
              ? [
                  {
                    organization: {
                      id: mcpAuth.orgId,
                      name: mcpAuth.orgName,
                      slug: '',
                    },
                  },
                ]
              : [],
          } as any,
          null,
          mcpAuth.token,
        );
        const mcpApiUrl = process.env.REACT_APP_API_URL;
        if (mcpApiUrl && !hasSpaCredentials()) {
          try {
            await fetchSpaCredentials(mcpAuth.token, mcpApiUrl);
          } catch (error) {
            console.error('Failed to fetch SPA credentials (MCP):', error);
            return;
          }
        }
        // Don't initialize clients against an empty credential holder
        // (initializeClients builds the GQL client from the read token).
        if (!hasSpaCredentials()) {
          console.error(
            'MCP headless mode: SPA credentials unavailable; cannot initialize Dato clients',
          );
          return;
        }
        rootStore.initialize({
          env: env || '',
          datoContext: datoCtx,
        });
        rootStore.datoClientStore.initializeClients();
        rootStore.writeQueueStore.reinitialize();
        setIsReady(true);
        return;
      }

      // If we have clerk auth, set up clerk context
      let clerkTokenValue: string | null = null;
      if (clerkSession) {
        try {
          clerkTokenValue = (await clerkSession.getToken()) || null;
          rootStore.userIdentityStore.setClerkContext(
            //@ts-ignore
            clerkUser,
            clerkSession,
            clerkTokenValue,
          );
        } catch (error) {
          console.error('Error fetching clerk token:', error);
          return;
        }
      }

      // Fetch SPA credentials (Dato read token + OpenAI key) before
      // any code reads them. Dato admin entrypoints supply their own
      // token via ctx and skip this fetch (no clerkToken on that path).
      const apiUrl = process.env.REACT_APP_API_URL;
      if (clerkTokenValue && apiUrl && !hasSpaCredentials()) {
        try {
          await fetchSpaCredentials(clerkTokenValue, apiUrl);
        } catch (error) {
          console.error('Failed to fetch SPA credentials:', error);
          return;
        }
      }

      // initializeClients() builds the DatoCMS GraphQL client from the
      // fetched read token (DatoClientStore.gqlAuthToken reads the holder),
      // and that holder is ONLY populated by fetchSpaCredentials above —
      // even the Dato-context path needs it, since the read token is no
      // longer baked into the bundle. If it isn't populated yet (Clerk
      // token not minted, fetch skipped/failed, or a stale sessionStorage
      // `ctx` driving the effect synchronously before Clerk hydrates), bail
      // and let the effect re-run rather than calling initializeClients()
      // against an empty holder (which throws and breaks the load).
      if (!hasSpaCredentials()) {
        return;
      }

      rootStore.initialize({
        env: env || '',
        datoContext: datoCtx,
      });

      if (!clerkSession && !datoCtx?.currentUser) {
        console.warn(
          'No valid user context found for datoPersonStore initialization',
        );
      }

      // Setup Clients before any code that reads from datoClientStore.gqlClient.
      // DatoPersonStore.initializePerson* synchronously walks to fetchPersonByEmail
      // before its first network await, which reads gqlClient — if that runs
      // before initializeClients(), the getter throws.
      rootStore.datoClientStore.initializeClients();

      // Re-initialize WriteQueueStore now that auth is available
      // (initial initialize() skips if no valid auth)
      rootStore.writeQueueStore.reinitialize();

      if (clerkSession && clerkUser) {
        rootStore.datoPersonStore.initializePersonFromClerkUser(clerkUser);
      } else if (datoCtx?.currentUser) {
        rootStore.datoPersonStore.initializePersonFromDatoUser(
          datoCtx.currentUser,
        );
      }

      setIsReady(true);
    }

    initializeStore();
  }, [env, datoCtx, clerkUser, clerkSession, isClerkLoaded, hasValidAuth]);

  return {
    isReady,
    userIdentity: rootStore.userIdentityStore.getUserIdentity(),
  };
}
